import {
  Eye,
  RotateCcw,
  ShoppingCart,
  CreditCard,
  Clock,
  CheckCircle2,
  XCircle,
  Undo2,
  AlertCircle,
} from "lucide-react";
import { Card } from "@components/ui/Card";
import type { Order, OrderStatus, OrderGateway } from "../../../types/order";

interface OrdersTableProps {
  orders: Order[];
  isLoading: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onView: (order: Order) => void;
  onRefund: (order: Order) => void;
}

const STATUS_BADGES: Record<OrderStatus, { bg: string; text: string; label: string }> = {
  paid: { bg: "bg-green-100", text: "text-green-700", label: "Paid" },
  pending: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Pending" },
  cancelled: { bg: "bg-gray-200", text: "text-gray-700", label: "Cancelled" },
  expired: { bg: "bg-orange-100", text: "text-orange-700", label: "Expired" },
  refunded: { bg: "bg-red-100", text: "text-red-700", label: "Refunded" },
};

const GATEWAY_LABEL: Record<OrderGateway, string> = {
};

const truncate = (value: string, head = 6, tail = 4): string => {
  if (!value) return "";
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
};

const formatDateTime = (iso: string): string => {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (value: number, currency: string | null): string => {
  const code = currency || "USD";
  return `${value.toFixed(2)} ${code}`;
};

const renderUser = (user: Order["user"]): {
  name: string;
  email: string;
} => {
  if (typeof user === "string") {
    return { name: "Unknown", email: "" };
  }
  const first = user.firstName || "";
  const last = user.lastName || "";
  const fullName = `${first} ${last}`.trim();
  return {
    name: fullName || user.email || "Unknown",
    email: user.email || "",
  };
};

export function OrdersTable({
  orders,
  isLoading,
  totalItems,
  currentPage,
  pageSize,
  totalPages,
  onPageChange,
  onView,
  onRefund,
}: OrdersTableProps) {
  if (isLoading) {
    return (
      <Card className="p-0 border shadow-sm">
        <div className="p-8 text-center text-gray-500">
          <div className="animate-spin inline-block w-6 h-6 border-[3px] border-current border-t-transparent text-indigo-600 rounded-full" />
          <p className="mt-2">Loading orders...</p>
        </div>
      </Card>
    );
  }

  const safeTotalPages = totalPages > 0 ? totalPages : 1;
  const showSummary = totalItems > 0;

  return (
    <Card className="p-0 border shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-[1100px] w-full text-left text-sm border-separate border-spacing-0">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Order ID
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                User
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Items
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Total
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Gateway
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Status
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100">
                Created
              </th>
              <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {orders.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No orders found.
                </td>
              </tr>
            ) : (
              orders.map((order) => {
                const user = renderUser(order.user);
                const badge = STATUS_BADGES[order.status] || STATUS_BADGES.pending;
                const gatewayLabel = order.gateway
                  ? GATEWAY_LABEL[order.gateway]
                  : "—";
                const itemsCount = order.lineItems.reduce(
                  (sum, item) => sum + item.quantity,
                  0,
                );
                const itemsDisplay = `${itemsCount} item${
                  itemsCount === 1 ? "" : "s"
                }`;
                const canRefund = order.status === "paid";
                return (
                  <tr
                    key={order._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-xs text-gray-700">
                          {truncate(order._id)}
                        </span>
                        {order.coupon?.code && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-semibold w-fit">
                            <TicketMini /> {order.coupon.code}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex flex-col">
                        <span className="font-medium text-[#102040]">
                          {user.name}
                        </span>
                        {user.email && (
                          <span className="text-xs text-gray-400">
                            {user.email}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-center gap-2 text-gray-700">
                        <ShoppingCart
                          size={14}
                          className="text-gray-400"
                          aria-hidden
                        />
                        <span className="text-sm">{itemsDisplay}</span>
                        {order.coupon && (
                          <span className="text-xs text-green-600">
                            −{order.coupon.discountUSD.toFixed(2)} USD
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <span className="font-semibold text-[#102040]">
                        {formatCurrency(order.totalUSD, order.currency)}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-top text-gray-600">
                      <div className="flex items-center gap-1.5">
                        {order.gateway && (
                          <GatewayIcon gateway={order.gateway} />
                        )}
                        <span>{gatewayLabel}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
                      >
                        <StatusIcon status={order.status} />
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 align-top text-[#989797]">
                      {formatDateTime(order.createdAt)}
                    </td>
                    <td className="px-6 py-4 align-top text-right">
                      <div className="inline-flex items-center gap-2">
                        <button
                          onClick={() => onView(order)}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                          title="View details"
                        >
                          <Eye size={14} />
                          View
                        </button>
                        <button
                          onClick={() => onRefund(order)}
                          disabled={!canRefund}
                          className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                            canRefund
                              ? "border-red-200 text-red-700 hover:bg-red-50"
                              : "border-gray-100 text-gray-300 cursor-not-allowed"
                          }`}
                          title={
                            canRefund
                              ? "Refund this order"
                              : "Only paid orders can be refunded"
                          }
                        >
                          <RotateCcw size={14} />
                          Refund
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showSummary && (
        <div className="px-3 sm:px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
          <span>
            Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} to{" "}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems} order
            {totalItems === 1 ? "" : "s"}
          </span>
          <div className="flex gap-1 ml-4">
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            >
              &lt; Previous
            </button>
            {Array.from({ length: Math.min(5, safeTotalPages) }, (_, i) => {
              let p = i + 1;
              if (safeTotalPages > 5 && currentPage > 3) {
                p = currentPage - 2 + i;
              }
              if (p > safeTotalPages) return null;
              return (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`px-3 py-1 rounded ${
                    currentPage === p
                      ? "bg-gray-900 text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === safeTotalPages}
              onClick={() =>
                onPageChange(Math.min(safeTotalPages, currentPage + 1))
              }
            >
              Next &gt;
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ---------- Inline status & gateway icons (avoid extra deps) ----------

function StatusIcon({ status }: { status: OrderStatus }) {
  switch (status) {
    case "paid":
      return <CheckCircle2 size={12} aria-hidden />;
    case "pending":
      return <Clock size={12} aria-hidden />;
    case "cancelled":
      return <XCircle size={12} aria-hidden />;
    case "expired":
      return <AlertCircle size={12} aria-hidden />;
    case "refunded":
      return <Undo2 size={12} aria-hidden />;
    default:
      return null;
  }
}

function GatewayIcon({ gateway }: { gateway: OrderGateway }) {
  const className = "w-4 h-4";
  if (gateway === "paytabs") {
    return <CreditCard className={className} aria-hidden />;
  }
  return null;
}


function TicketMini() {
  // Tiny inline ticket glyph so we don't import another lucide icon
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 1 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 1 0 0-4V9z" />
      <path d="M13 7v10" strokeDasharray="2 2" />
    </svg>
  );
}
